import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Checkbox, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Divider, Card, Stack, CircularProgress, Button, FormControl, InputLabel, Select, ToggleButton, ToggleButtonGroup,
  ListItemText, OutlinedInput, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Tooltip
} from "@mui/material";
import { Delete, Person, School, Save, Add, History, Edit, AssignmentTurnedIn } from "@mui/icons-material";
import {
  collection, getDocs, where, doc, getDoc, getFirestore,
  query, orderBy, writeBatch, Timestamp,
  updateDoc, arrayUnion, arrayRemove,
  addDoc,
  serverTimestamp,
  getCountFromServer,
  runTransaction
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";
import { Autocomplete } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function Feecollection() {
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("single");
  const [schoolId, setSchoolId] = useState(null);
  const [students, setStudents] = useState([]);
  const [discountTypes, setDiscountTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [ledgers, setledgers] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState([{ typeId: "", amount: 0 }]);

  const [installmentCount, setInstallmentCount] = useState(1);
  const [manualInstallments, setManualInstallments] = useState([]);
  const [feeHeadMaster, setFeeHeadMaster] = useState([]); // All available heads
  const [customHead, setCustomHead] = useState({ headId: "", name: "", amount: "" });
  const [headWiseInstallments, setHeadWiseInstallments] = useState({});
  const [selectedHeadId, setSelectedHeadId] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [sendWhatsapp, setSendWhatsapp] = useState(false); // default NO
  const [showPayModal, setShowPayModal] = useState(false);
  const [activeInstallmentIndex, setActiveInstallmentIndex] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [saving, setSaving] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState({
    paidAmount: 0,
    payMode: "Cash",
    bankId: "",
    bankName: "",
    bankBranch: "",
    bankCode: "",
    transactionId: "",
    chequeNo: "",
    date: new Date().toISOString().split('T')[0],
    note: "",
    ledger: "",
    receiptNo: `REC-${Date.now().toString().slice(-6)}`
  });

  const [paymentHistory, setPaymentHistory] = useState([]); // To store actual receipts
  const subTotal = feeHeads.filter(h => h.checked).reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
  const totalDiscount = appliedDiscounts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const structuredHeads = useMemo(() => {
    return feeHeads.filter(h => h.checked).map(head => {
      if (head.installment === "Yes") {
        const installments = headWiseInstallments?.[head.headId] || [];
        return {
          headId: head.headId,
          name: head.name,
          installments: installments.map((inst, i) => ({
            ...inst,
            index: i
          }))
        };
      }
      return {
        headId: head.headId,
        name: head.name,
        installments: [
          {
            index: `normal_${head.headId}`,
            name: head.name,
            amount: parseFloat(head.amount || 0),
            dueDate: "",
            type: "normal"
          }
        ]
      };
    });

  }, [feeHeads, headWiseInstallments]);

  const getInstallmentStatus = (
    headId,
    installmentIndex,
    installmentAmount
  ) => {

    let paid = 0;
    paymentHistory.forEach((p) => {
      if (
        String(p.headId) === String(headId) &&
        String(p.installmentIndex) === String(installmentIndex)
      ) {
        paid += parseFloat(p.paidAmount || 0);
      }
      else if (Array.isArray(p.multiHeads)) {
        const matched = p.multiHeads.find(mh => String(mh.headId) === String(headId) && String(mh.installmentIndex) === String(installmentIndex));
        if (matched) {
          paid += parseFloat(matched.paidAmount || 0);
        }
      }
    });

    return {
      paid,
      remaining: Math.max(0, installmentAmount - paid)
    };
  };

  const financeSummary = useMemo(() => {
    if (!selectedStudent) {
      return { totalFees: 0, totalDisc: 0, totalPaid: 0, remaining: 0 };
    }
    const totalFees = feeHeads.filter(h => h.checked).reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
    const totalDisc = parseFloat(totalDiscount) || 0;
    const totalPaid = paymentHistory.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
    const remaining = totalFees - totalDisc - totalPaid;
    return { totalFees, totalDisc, totalPaid, remaining };
  }, [selectedStudent, feeHeads, totalDiscount, paymentHistory]);

  useEffect(() => { fetchInitialData(); }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data().schoolId;
      setSchoolId(sId);
      const courseQuery = query(collection(db, "courses"), where("schoolId", "==", sId), orderBy('sequence', 'asc'));
      const [admSnap, studentSnap, courseSnap, discSnap, ledgerSnap] = await Promise.all([
        getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("status", "==", "Active"))),
        getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
        getDocs(courseQuery),
        getDocs(query(collection(db, "discountTypes"), where("schoolId", "==", sId))),
        getDocs(query(collection(db, "ledgers")))
      ]);

      const studentMap = {};
      studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });
      const courseList = courseSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCourses(courseList);
      const courseMap = {};
      courseList.forEach(c => { courseMap[c.id] = c.name; });
      setDiscountTypes(discSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setledgers(ledgerSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const merged = admSnap.docs.map(doc => {
        const adm = { id: doc.id, ...doc.data() };
        const sData = studentMap[adm.studentId] || {};
        return {
          ...adm,
          fullName: `${sData.firstname || ''} ${sData.fathername || ''} ${sData.surname || ''}`.trim().toUpperCase(),
          className: courseMap[adm.class] || 'N/A',
          studentPhoto: sData.photoURL
        };
      });
      setStudents(merged);
      const headsSnap = await getDocs(query(collection(db, "feeheads"), where("schoolId", "==", sId), orderBy('sequence_no', 'asc')));

      setFeeHeadMaster(headsSnap.docs.map(d => ({
        id: d.id,
        fee_heads: d.data().fee_heads, // Use the exact field name from Firestore
        defaultAmount: d.data().amount || 0
      })));
      const bankSnap = await getDocs(query(collection(db, "banks"), where("schoolId", "==", sId)));

      setBanks(
        bankSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      );


    } catch (e) {
      console.error("Fetch Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = async (val, type) => {
    if (!val) return;
    setFeeHeads([]);
    if (type === 'student') {
      setSelectedStudent(val);
      console.log(val);
      const exists = await fetchExistingStructure(val.id);
      if (!exists) fetchClassHeads(val.class);
    } else {
      setSelectedClass(val);
      // In Bulk Mode, filter students of this class and select all by default
      const classStudents = students.filter(s => s.class === val);
      setSelectedStudentIds(classStudents.map(s => s.id));

      const exists = await fetchExistingStructure(val, true);
      if (!exists) fetchClassHeads(val);
    }
  };

  const fetchExistingStructure = async (id, isClass = false) => {
    const q = query(collection(db, "fee_structures"), where(isClass ? "classId" : "admissionId", "==", id));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      setHeadWiseInstallments(data.headWiseInstallments || {}); // ✅ ADD THIS

      const sortedHeads = (data.heads || []).sort((a, b) =>
        (Number(a.sequence_no) || 0) - (Number(b.sequence_no) || 0)
      );
      setFeeHeads(sortedHeads);

      // 2. Set Discounts
      setAppliedDiscounts(data.discounts || [{ typeId: "", amount: 0 }]);

      // 3. IMPORTANT: Set existing installments and count
      if (data.installments && data.installments.length > 0) {
        setInstallmentCount(data.installments.length);
        setManualInstallments(data.installments);
      }

      setPaymentHistory(data.paymentHistory || []);

      if (data.installments && data.installments.length > 0) {
        setInstallmentCount(data.installments.length);
        setManualInstallments(data.installments);
      }
      return true;
    }
    return false;
  };

  const totalDueAmount = useMemo(() => {
    // 1. Total Base Fees (Checked heads)
    const baseFees = feeHeads.filter(h => h.checked).reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);

    // 2. Total Discounts
    const totalDisc = appliedDiscounts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    // 3. Total Paid (from History)
    const totalPaid = paymentHistory.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);

    return baseFees - totalDisc - totalPaid;
  }, [feeHeads, appliedDiscounts, paymentHistory]);

  const fetchClassHeads = async (classId) => {
    const q = query(
      collection(db, "feeHeadAssignments"),
      where("classId", "==", classId),
    );
    const snap = await getDocs(q);
    setFeeHeads(snap.docs.map(doc => ({
      headId: doc.data().feeHeadId,
      name: doc.data().feeHeadName,
      amount: doc.data().amount,
      sequence_no: doc.data().sequence_no || 0, // Store sequence for UI sorting
      checked: true
    })));
  };



  // Add this handler to allow manual editing of amounts
  const handleInstallmentChange = (index, field, value) => {
    const updated = [...manualInstallments];
    updated[index] = {
      ...updated[index],
      [field]: field === 'amount' ? (parseFloat(value) || 0) : value
    };
    setManualInstallments(updated);
  };

  useEffect(() => {
    if (subTotal <= 0) return;

    if (manualInstallments.length === installmentCount) {
      return;
    }

    // Auto-calculation logic (only runs if count changes or is new)
    const amountPerInst = Math.floor(subTotal / installmentCount);
    const remainder = subTotal - (amountPerInst * installmentCount);

    const newInst = Array.from({ length: installmentCount }, (_, i) => {
      const isLast = i === installmentCount - 1;
      return {
        index: i + 1,
        amount: isLast ? (amountPerInst + remainder) : amountPerInst,
        dueDate: "",
        isLast: isLast,
        applicableDiscount: isLast ? totalDiscount : 0
      };
    });
    setManualInstallments(newInst);
  }, [subTotal, installmentCount]);

  useEffect(() => {
    setManualInstallments(prev => prev.map(inst =>
      inst.isLast ? { ...inst, applicableDiscount: totalDiscount } : inst
    ));
  }, [totalDiscount]);


  const studentsInClass = useMemo(() => {
    return students.filter(s => s.class === selectedClass);
  }, [students, selectedClass]);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="80vh"><CircularProgress /></Box>;

  const addCustomHead = () => {
    if (!customHead.headId || !customHead.amount) {
      return Swal.fire("Error", "Please select a head and enter amount", "error");
    }

    if (feeHeads.some(h => h.headId === customHead.headId)) {
      return Swal.fire("Note", "This fee head is already added", "info");
    }

    // Find the master record to get the sequence number
    const masterRecord = feeHeadMaster.find(m => m.id === customHead.headId);

    const newHead = {
      headId: customHead.headId,
      name: customHead.name,
      amount: parseFloat(customHead.amount),
      checked: true,
      isCustom: true,
      sequence_no: masterRecord?.sequence_no || 99 // Default to end of list if missing
    };

    setFeeHeads(prev => [...prev, newHead]);
    setCustomHead({ headId: "", name: "", amount: "" });
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setSelectedClass("");
    setSelectedStudentIds([]);
    setFeeHeads([]);
    setAppliedDiscounts([{ typeId: "", amount: 0 }]);
    setInstallmentCount(1);
    setManualInstallments([]);
  };

  const handleProcessPayment = async () => {
    if (paymentDetails.paidAmount <= 0) return Swal.fire("Error", "Enter valid amount", "error");

    const newPayment = {
      receiptNo: paymentDetails.receiptNo,
      bookReceiptNo: paymentDetails.bookReceiptNo,
      paidAmount: parseFloat(paymentDetails.paidAmount),
      date: paymentDetails.date,
      payMode: paymentDetails.payMode,
      note: paymentDetails.note,
      createdAt: Timestamp.now(),
      installmentIndex: activeInstallmentIndex,
      multiHeads: selectedPayments,
    };

    try {
      const studentRef = doc(db, "fee_structures", mode === 'single' ? selectedStudent.id : selectedClass);

      // Use arrayUnion to keep a history of every transaction (Receipts)
      await updateDoc(studentRef, {
        paymentHistory: arrayUnion(newPayment),
        lastPaymentDate: paymentDetails.date,
        // Update the installment status if needed
      });

      setPaymentHistory(prev => [...prev, newPayment]);
      setShowPayModal(false);
      Swal.fire("Success", "Payment Recorded & Receipt Generated", "success");
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // 1. Handle Date Changes for Installments
  const handleInstallmentDateChange = (index, newDate) => {
    const updated = [...manualInstallments];
    updated[index] = { ...updated[index], dueDate: newDate };
    setManualInstallments(updated);
  };

  // 3. View Receipts (For now, just a console log or alert)
  const viewReceipts = (index) => {
    const receipts = paymentHistory.filter(p => p.installmentIndex === index);
    if (receipts.length === 0) return Swal.fire("No Payments", "No transactions found for this installment", "info");

    const list = receipts.map(r => `${r.receiptNo}: ₹${r.amount} (${r.payMode})`).join('\n');
    Swal.fire(`Payment History - Inst ${index + 1}`, list, "info");
  };

  const deleteReceipt = async (receiptObject) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will revert the paid amount!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const studentRef = doc(db, "fee_structures", selectedStudent.id);
        await updateDoc(studentRef, {
          paymentHistory: arrayRemove(receiptObject)
        });

        setPaymentHistory(prev => prev.filter(r => r.receiptNo !== receiptObject.receiptNo));
        Swal.fire("Deleted!", "Receipt has been removed.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete receipt", "error");
      }
    }
  };

  const generateNextReceiptNo = async (sId) => {
    try {
      if (!sId) return `REC-${Date.now().toString().slice(-6)}`;
      const q = query(collection(db, "fee_structures"), where("schoolId", "==", sId));
      const snap = await getDocs(q);

      let totalReceiptsCount = 0;
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.paymentHistory && Array.isArray(data.paymentHistory)) {
          totalReceiptsCount += data.paymentHistory.length;
        }
      });
      let nextNum = totalReceiptsCount + 1;
      return `REC-${nextNum}`;
    } catch (error) {
      console.error("Error generating receipt number from collection count:", error);
      return `REC-${Date.now().toString().slice(-6)}`; // Safe fallback timestamp
    }
  };

  const handleFinalSubmitPayment = async () => {
    if (saving) return;

    setSaving(true);

    const amountToPay = parseFloat(paymentDetails.paidAmount || 0);
    const isMultiPayment = selectedPayments.length > 0;
    if (amountToPay <= 0) {
      return Swal.fire({
        title: "Invalid Amount",
        text: "Please enter valid amount",
        icon: "warning"
      });
    }

    let allowedLimit = 0;
    if (isMultiPayment) {
      allowedLimit = selectedPayments.reduce((sum, item) => {
        const head = structuredHeads.find(h => h.headId === item.headId);
        if (!head) return sum;
        const inst = head.installments.find(i => String(i.index) === String(item.installmentIndex));
        if (!inst) return sum;
        const installmentAmount = parseFloat(inst.finalAmount || inst.amount || 0);
        const { paid } = getInstallmentStatus(
          item.headId,
          item.installmentIndex,
          installmentAmount
        );
        const remaining = Math.max(0, installmentAmount - paid);
        return sum + Math.max(0, remaining);
      }, 0);
    }

    else {
      const instAmount = parseFloat(paymentDetails.currentAmount) || 0;
      const alreadyPaid = paymentHistory.filter(p => p.headId === selectedHeadId && parseInt(p.installmentIndex) === parseInt(activeInstallmentIndex))
        .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
      allowedLimit = instAmount - alreadyPaid;
    }
    if (amountToPay > allowedLimit) {
      return Swal.fire({
        title: "Overpayment",
        text: `Maximum payable amount is ₹${allowedLimit}`,
        icon: "warning"
      });
    }
    try {

      const studentRef = doc(db, "fee_structures", selectedStudent.id);
      const newReceipt = {
        paidAmount: amountToPay,
        date: paymentDetails.date,
        payMode: paymentDetails.payMode,
        bankId: paymentDetails.bankId || "",
        bankName: paymentDetails.bankName || "",
        bankBranch: paymentDetails.bankBranch || "",
        bankCode: paymentDetails.bankCode || "",
        transactionId: paymentDetails.transactionId || "",
        chequeNo: paymentDetails.chequeNo || "",
        note: paymentDetails.note || "",
        receiptNo: paymentDetails.receiptNo,
        ledger: paymentDetails.ledger,
        bookReceiptNo: paymentDetails.bookReceiptNo || "",
        timestamp: new Date().toISOString(),
        recordedBy: auth.currentUser?.uid,
      };
      if (!isMultiPayment) {
        newReceipt.headId = selectedHeadId;
        newReceipt.installmentIndex = activeInstallmentIndex;
      }
      else {
        newReceipt.multiHeads = selectedPayments.map(item => {
          const head = structuredHeads.find(h => String(h.headId) === String(item.headId));
          const inst = head?.installments?.find(i => String(i.index) === String(item.installmentIndex));
          const originalAmount = parseFloat(inst?.finalAmount || inst?.amount || 0);
          const isLastInstallment = head.installments.findIndex(x => String(x.index) === String(item.installmentIndex)
          ) === head.installments.length - 1;
          const discount = isLastInstallment ? (parseFloat(totalDiscount) || 0) : 0;
          let totalAmount = originalAmount;
          if (head.headId === "74be50bf-71ea-4ad1-9a12-64fd4ab96fb5") {
            totalAmount = Math.max(0, originalAmount - discount);
          }

          const { paid } = getInstallmentStatus(
            item.headId,
            item.installmentIndex,
            totalAmount
          );

          const remaining =
            Math.max(0, totalAmount - paid);

          return {
            headId: item.headId,
            installmentIndex: item.installmentIndex,
            paidAmount: remaining
          };

        });

      }

      await updateDoc(studentRef, {
        paymentHistory: arrayUnion(newReceipt)
      });

      setPaymentHistory(prev => [...prev, newReceipt]);
      if (sendWhatsapp) {
        const mobile = selectedStudent?.father_mobile1
        if (mobile) {
          const updatedPaid = paymentHistory.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0) + amountToPay;
          const totalFees = feeHeads.filter(h => h.checked).reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
          const totalDisc = parseFloat(totalDiscount) || 0;
          const balance = totalFees - totalDisc - updatedPaid;
          await sendSingleWhatsApp(mobile, {
            parentName: selectedStudent?.fullName || "Parent",
            paidDate: paymentDetails.date,
            paidAmount: amountToPay,
            balance: Math.max(0, balance)
          });
        }
      }

      setSelectedPayments([]);

      setShowPayModal(false);

      Swal.fire(
        "Success",
        `Receipt ${newReceipt.receiptNo} Generated`,
        "success"
      );

    } catch (error) {

      Swal.fire(
        "Error",
        error.message,
        "error"
      )
    }
    finally {
      setLoading(false);
      setSaving(false);
    }

  };

  const sendSingleWhatsApp = async (mobile, data) => {
    try {
      // 1. Wallet Balance Check
      const walletDoc = await getDoc(doc(db, "wallets", "0YVnAJmiq8cajkf4ufE5a8xYoFQ2"));
      const totalMessages = walletDoc.exists() ? walletDoc.data().totalMessages : 0;

      const historyRef = collection(db, "whatsapphistory");
      const historySnapshot = await getCountFromServer(query(historyRef));
      const sentCount = historySnapshot.data().count;

      if (totalMessages - sentCount <= 0) {
        console.error("WhatsApp Wallet Balance is Low");
        return;
      }

      // 2. Fetch Config
      const whatsappSnap = await getDocs(collection(db, "whatsapp"));
      if (whatsappSnap.empty) return;

      const { phoneid: wabaid, accesstoken } = whatsappSnap.docs[0].data();
      let status = "failed";

      const payload = {
        messaging_product: "whatsapp",
        to: `91${mobile}`,
        type: "template",
        template: {
          name: "student_payment",
          language: { code: "en" },
          components: [
            {
              type: "body",
              parameters: [
                { type: "text", text: data.parentName },   // {{1}}
                { type: "text", text: data.paidDate },     // {{2}}
                { type: "text", text: String(data.paidAmount) }, // {{3}}
                { type: "text", text: String(data.balance) }     // {{4}}
              ],
            }
          ],
        },
      };

      // 4. API Call
      const r = await fetch(`https://graph.facebook.com/v22.0/${wabaid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accesstoken}`,
        },
        body: JSON.stringify(payload),
      });

      const res = await r.json();
      if (res.messages?.[0]?.id) status = "sent";

      // 5. Save History
      await addDoc(collection(db, "whatsapphistory"), {
        mobile: mobile,
        studentName: selectedStudent.fullName,
        postTitle: "Payment Success Receipt",
        status,
        timestamp: new Date().toISOString(),
      });

    } catch (err) {
      console.error("WhatsApp Task Failed:", err);
    }
  };

  const openPaymentModal = async ({ headId, installmentIndex, remaining, amount }) => {
    setActiveInstallmentIndex(installmentIndex);
    setSelectedHeadId(headId);

    // Fetch the actual globally generated unique receipt number
    const globalNextReceiptNo = await generateNextReceiptNo(schoolId);

    setPaymentDetails({
      paidAmount: remaining,
      payMode: "Cash",
      date: new Date().toISOString().split("T")[0],
      receiptNo: globalNextReceiptNo,
      bookReceiptNo: "",
      currentAmount: amount
    });

    setShowPayModal(true);
  };

  const handleFullPayment = () => {
    let totalRemaining = 0;
    const pendingSelections = [];
    structuredHeads.forEach((head) => {
      head.installments.forEach((inst, i) => {
        const originalAmount =
          parseFloat(inst.finalAmount || inst.amount || 0);

        const isLastInstallment =
          i === head.installments.length - 1;

        const discount =
          isLastInstallment
            ? (parseFloat(totalDiscount) || 0)
            : 0;

        let installmentAmount = originalAmount;

        // APPLY DISCOUNT ONLY TO TARGET HEAD
        if (head.headId === "74be50bf-71ea-4ad1-9a12-64fd4ab96fb5") {
          installmentAmount = Math.max(0, originalAmount - discount);
        }
        const { paid } = getInstallmentStatus(
          head.headId,
          inst.index,
          installmentAmount
        );

        const remaining = Math.max(0, installmentAmount - paid);
        let isLocked = false;
        if (i > 0) {

          const prevInst = head.installments[i - 1];

          const prevOriginal =
            parseFloat(
              prevInst.finalAmount ||
              prevInst.amount ||
              0
            );

          const prevIsLast =
            (i - 1) === head.installments.length - 1;

          const prevDiscount =
            prevIsLast
              ? (parseFloat(totalDiscount) || 0)
              : 0;

          let prevAmount = prevOriginal;

          // APPLY DISCOUNT TO TARGET HEAD
          if (head.headId === "74be50bf-71ea-4ad1-9a12-64fd4ab96fb5") {
            prevAmount = Math.max(0, prevOriginal - prevDiscount);
          }

          const prevPaid = paymentHistory
            .filter(
              p =>
                String(p.headId) === String(head.headId) &&
                String(p.installmentIndex) === String(prevInst.index)
            )
            .reduce(
              (sum, p) => sum + (parseFloat(p.paidAmount) || 0),
              0
            );

          const prevRemaining =
            Math.max(0, prevAmount - prevPaid);

          if (prevRemaining > 0) {
            isLocked = true;
          }

        }

        // ONLY INCLUDE PAYABLE INSTALLMENTS
        if (Math.round(remaining) > 0 && !isLocked) {

          totalRemaining += remaining;

          pendingSelections.push({
            headId: head.headId,
            installmentIndex: inst.index,
            remaining
          });

        }

      });

    });

    if (totalRemaining <= 0) {

      return Swal.fire(
        "Settled",
        "All fees are already paid",
        "info"
      );

    }

    Swal.fire({
      title: 'Bulk Payment',
      text: `Clear all remaining fees (₹${totalRemaining.toLocaleString()})?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Open Bulk Pay'
    }).then((result) => {

      if (result.isConfirmed) {

        setSelectedPayments(pendingSelections);

        setPaymentDetails(prev => ({
          ...prev,
          paidAmount: totalRemaining,
          receiptNo: generateNextReceiptNo(),
          bookReceiptNo: "",
          note: "Full Bulk Settlement"
        }));

        setShowPayModal(true);

      }

    });

  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const requestDelete = async (receipt) => {
    const { value: note, isConfirmed } = await Swal.fire({
      title: 'Request Deletion?',
      input: 'text',
      inputLabel: 'Reason for deletion',
      inputPlaceholder: 'e.g., Duplicate entry...',
      showCancelButton: true,
      confirmButtonColor: '#b22222',
      inputValidator: (value) => !value && 'Please provide a reason!'
    });

    if (isConfirmed && note) {
      try {
        const admissionId = selectedStudent.admissionId || selectedStudent.id;
        if (!admissionId) throw new Error("Student ID missing");

        // FIX: Use receiptNo instead of receipt.id
        const receiptIdentifier = receipt.receiptNo;
        if (!receiptIdentifier) throw new Error("Receipt Number missing");

        await addDoc(collection(db, "fee_approvals"), {
          type: 'DELETE_PAYMENT',
          status: 'Pending',
          requestedBy: auth.currentUser.uid,
          createdAt: Timestamp.now(),
          admissionId: admissionId,
          studentName: selectedStudent.fullName || selectedStudent.name,
          amount: receipt.paidAmount,
          receiptNo: receiptIdentifier, // Use receiptNo here
          date: receipt.date,
          mode: receipt.payMode,
          note: note
        });

        Swal.fire("Sent", "Delete request sent successfully", "success");
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };
  // Requesting Approval for Edit
  const requestEdit = async (receipt) => {
    // 1. Capture New Amount and Note
    const { value: formValues } = await Swal.fire({
      title: `Edit Receipt ${receipt.receiptNo}`,
      html:
        `<div style="text-align:left; font-size:14px; margin-bottom:5px;">New Amount:</div>` +
        `<input id="swal-amount" class="swal2-input" type="number" value="${receipt.paidAmount}">` +
        `<div style="text-align:left; font-size:14px; margin-bottom:5px; margin-top:10px;">Reason for change:</div>` +
        `<input id="swal-note" class="swal2-input" placeholder="e.g., Wrong amount entered">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Send Request',
      preConfirm: () => {
        const amount = document.getElementById('swal-amount').value;
        const note = document.getElementById('swal-note').value;
        if (!amount || amount <= 0) return Swal.showValidationMessage('Enter a valid amount');
        if (!note) return Swal.showValidationMessage('Note is required');
        return { amount: parseFloat(amount), note: note };
      }
    });

    if (formValues) {
      try {
        // FIXING THE UNDEFINED ERROR:
        // If your receipt object doesn't have .id, use .receiptNo or .timestamp as a fallback 
        // so Firebase doesn't crash, but ideally, use the document ID.
        const collectionId = receipt.id || receipt.receiptNo || "unknown_id";
        const admissionId = selectedStudent.admissionId || selectedStudent.id;

        if (!admissionId) throw new Error("Student ID is missing");

        await addDoc(collection(db, "fee_approvals"), {
          type: 'EDIT_PAYMENT',
          status: 'Pending',
          requestedBy: auth?.currentUser?.uid || 'anonymous',
          createdAt: serverTimestamp(), // Use serverTimestamp for better sorting
          admissionId: admissionId,
          studentName: selectedStudent.fullName || selectedStudent.name,
          collectionId: collectionId, // This will no longer be undefined
          oldAmount: receipt.paidAmount || 0,
          newAmount: formValues.amount,
          receiptNo: collectionId,
          date: receipt.date || '',
          mode: receipt.payMode || 'Cash',
          note: formValues.note
        });

        Swal.fire("Requested", "Edit request sent to Admin", "success");
      } catch (e) {
        console.error("Submission Error:", e);
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  const handleMultiPay = () => {

    if (selectedPayments.length === 0) {

      return Swal.fire(
        "No Selection",
        "Please select installments",
        "warning"
      );

    }

    let total = 0;

    selectedPayments.forEach((item) => {

      const head = structuredHeads.find(
        h => String(h.headId) === String(item.headId)
      );

      if (!head) return;

      // FIND INSTALLMENT PROPERLY
      const inst = head.installments.find(
        i => String(i.index) === String(item.installmentIndex)
      );

      if (!inst) return;

      const originalAmount =
        parseFloat(inst.finalAmount || inst.amount || 0);

      const isLastInstallment =
        head.installments.findIndex(
          x => String(x.index) === String(inst.index)
        ) === head.installments.length - 1;

      const discount =
        isLastInstallment
          ? (parseFloat(totalDiscount) || 0)
          : 0;

      let installmentAmount = originalAmount;

      // APPLY DISCOUNT ONLY TO TARGET HEAD
      if (head.headId === "74be50bf-71ea-4ad1-9a12-64fd4ab96fb5") {
        installmentAmount = Math.max(0, originalAmount - discount);
      }

      // CALCULATE ALREADY PAID
      const { paid, remaining } = getInstallmentStatus(
        item.headId,
        item.installmentIndex,
        installmentAmount
      );

      console.log(paid);
      console.log(remaining);

      total += remaining;

    });




    if (total <= 0) {

      return Swal.fire(
        "Settled",
        "Selected installments already paid",
        "info"
      );

    }

    setPaymentDetails(prev => ({
      ...prev,
      paidAmount: total,
      receiptNo: generateNextReceiptNo(),
      bookReceiptNo: "",
      note: "Bulk Installment Payment"
    }));

    setShowPayModal(true);

  };


  const handleDirectFieldUpdate = async (receipt) => {
    const ledgerOptionsHtml = ledgers.map(l =>
      `<option value="${l.id}" ${String(l.id) === String(receipt.ledger) ? 'selected' : ''}>${l.ledgername}</option>`
    ).join('');

    const bankOptionsHtml = banks.map(b =>
      `<option value="${b.id}" ${String(b.id) === String(receipt.bankId) ? 'selected' : ''}>${b.bankName}</option>`
    ).join('');

    const modes = ["Cash", "Bank Transfer", "UPI", "Cheque", "Card"];
    const modeOptionsHtml = modes.map(m =>
      `<option value="${m}" ${m === receipt.payMode ? 'selected' : ''}>${m}</option>`
    ).join('');

    const { value: formValues } = await Swal.fire({
      title: `Quick Edit Fields (No Approval Required)`,
      width: '600px',
      html: `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; text-align: left; font-size: 13px;">
        <div>
          <label style="font-weight: bold; color: #475569;">Transaction Date:</label>
          <input id="swal-direct-date" class="swal2-input" type="date" value="${receipt.date || ''}" style="margin: 4px 0 0 0; width: 100%;">
        </div>
        <div>
          <label style="font-weight: bold; color: #94a3b8;">Collection Amount (Locked):</label>
          <input class="swal2-input" type="text" value="₹${receipt.paidAmount}" disabled style="margin: 4px 0 0 0; width: 100%; background: #f1f5f9; color: #94a3b8; cursor: not-allowed;">
        </div>
        <div>
          <label style="font-weight: bold; color: #475569;">System Receipt No:</label>
          <input id="swal-direct-receiptNo" class="swal2-input" type="text" value="${receipt.receiptNo || ''}" style="margin: 4px 0 0 0; width: 100%;">
        </div>
        <div>
          <label style="font-weight: bold; color: #475569;">Book Receipt No:</label>
          <input id="swal-direct-bookReceipt" class="swal2-input" type="text" value="${receipt.bookReceiptNo || ''}" style="margin: 4px 0 0 0; width: 100%;">
        </div>
        <div style="grid-column: span 2;">
          <label style="font-weight: bold; color: #475569;">Target Accounting Ledger:</label>
          <select id="swal-direct-ledger" class="swal2-input" style="margin: 4px 0 0 0; width: 100%; display: block;">
            <option value="">-- Choose Book Ledger --</option>
            ${ledgerOptionsHtml}
          </select>
        </div>
        <div style="grid-column: span 2;">
          <label style="font-weight: bold; color: #475569;">Note:</label>
          <input id="swal-direct-note" class="swal2-input" value="${receipt.note || ''}" style="margin: 4px 0 0 0; width: 100%;">
        </div>
      </div>
    `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Save Changes Instantly',
      preConfirm: () => {
        const date = document.getElementById('swal-direct-date').value;
        const receiptNo = document.getElementById('swal-direct-receiptNo').value;
        const bookReceiptNo = document.getElementById('swal-direct-bookReceipt').value;
        const payMode = document.getElementById('swal-direct-mode').value;
        const bankId = document.getElementById('swal-direct-bank').value;
        const ledger = document.getElementById('swal-direct-ledger').value;
        const note = document.getElementById('swal-direct-note').value;
        if (!date || !receiptNo || !ledger) {
          return Swal.showValidationMessage('Date, Receipt No, and Ledger fields are required.');
        }
        const selectedBankObj = banks.find(b => String(b.id) === String(bankId));
        return {
          ...receipt,
          date,
          receiptNo,
          bookReceiptNo,
          ledger,
          note
        };
      }
    });

    if (formValues) {
      try {
        const studentRef = doc(db, "fee_structures", selectedStudent.id);
        await updateDoc(studentRef, {
          paymentHistory: arrayRemove(receipt)
        });
        await updateDoc(studentRef, {
          paymentHistory: arrayUnion(formValues)
        });
        setPaymentHistory(prev => prev.map(r => r.receiptNo === receipt.receiptNo ? formValues : r));
        Swal.fire("Updated Successfully", "Metadata changes applied directly to database documents.", "success");
      } catch (e) {
        console.error(e);
        Swal.fire("Update Error", e.message, "error");
      }
    }
  };

  return (
    <Box bgcolor="#f4f7f9" minHeight="100vh">
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        {/* <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, v) => v && setMode(v)} color="primary">
            <ToggleButton value="single"><Person sx={{ mr: 1 }} /> Account Section</ToggleButton>
            <ToggleButton value="bulk"><School sx={{ mr: 1 }} /> Bulk Class</ToggleButton>
          </ToggleButtonGroup>
        </Stack> */}

        <Grid className="row" spacing={2}>
          <Grid className="col-sm-5" item xs={12} md={6}>
            {/* <Typography variant="subtitle2" gutterBottom>1. Student Selection</Typography> */}
            <Autocomplete
              options={students}
              getOptionLabel={(opt) => `${opt.fullName} (${opt.className})`}
              onChange={(e, val) => handleSelectionChange(val, 'student')}
              renderInput={(params) => <TextField {...params} label="Search Student Name" size="small" fullWidth />}
            />
            {selectedStudent && (
              <Box sx={{ mt: 2 }}>
                {/* TOP TIER: STUDENT INFO HEADER */}
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '16px 16px 4px 4px', // Rounded top, flatter bottom to "connect" with stats
                    background: 'linear-gradient(90deg, #fff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <Avatar
                      src={selectedStudent.studentPhoto}
                      sx={{
                        width: 60,
                        height: 60,
                        border: '2px solid #fff',
                        boxShadow: '0 0 15px rgba(178, 34, 34, 0.2)',
                        bgcolor: '#b22222',
                        fontWeight: 800
                      }}
                    >
                      {selectedStudent.fullName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                        {selectedStudent.fullName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, mt: 0.5, display: 'block' }}>
                        {selectedStudent.className}
                      </Typography>
                    </Box>
                  </Stack>

                  <Chip
                    label={financeSummary.remaining > 0 ? "PAYMENT PENDING" : "FULLY PAID"}
                    size="small"
                    sx={{
                      bgcolor: financeSummary.remaining > 0 ? '#fff1f2' : '#f0fdf4',
                      color: financeSummary.remaining > 0 ? '#e11d48' : '#16a34a',
                      fontWeight: 900,
                      fontSize: '0.65rem',
                      border: '1px solid',
                      borderColor: 'inherit'
                    }}
                  />
                </Card>

                {/* BOTTOM TIER: FINANCIAL METRICS TILES */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: '1px', // Creates thin border effect between tiles
                    bgcolor: '#e2e8f0',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px 4px 16px 16px',
                    overflow: 'hidden',
                    mt: '2px'
                  }}
                >
                  {/* Total Fees Tile */}
                  <Box sx={{ bgcolor: '#fff', p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                      Total Fees
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#334155' }}>
                      ₹{financeSummary.totalFees.toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Discount Tile */}
                  <Box sx={{ bgcolor: '#fff', p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                      Discounts
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#ef4444' }}>
                      -₹{financeSummary.totalDisc.toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Paid Tile */}
                  <Box sx={{ bgcolor: '#fff', p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                      Paid
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#16a34a' }}>
                      ₹{financeSummary.totalPaid.toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Balance Tile - Highlighted */}
                  <Box sx={{
                    bgcolor: financeSummary.remaining > 0 ? '#fff1f2' : '#f0fdf4',
                    p: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{
                      color: financeSummary.remaining > 0 ? '#be123c' : '#15803d',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      fontSize: '0.6rem'
                    }}>
                      Balance Due
                    </Typography>
                    <Typography variant="h6" sx={{
                      fontWeight: 900,
                      color: financeSummary.remaining > 0 ? '#b22222' : '#16a34a'
                    }}>
                      ₹{Math.max(0, financeSummary.remaining).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <Card variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box p={1}>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Particulars</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feeHeads
                        .sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0)) // Sorting logic
                        .map((h, i) => (
                          <TableRow key={h.headId || i}>
                            <TableCell>
                              {h.name}
                            </TableCell>
                            <TableCell align="right">₹{h.amount}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Card>

          </Grid>
          <Grid className="col-sm-7" item xs={12} lg={7}>
            <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
              <Box sx={{ p: 1, bgcolor: '#1e293b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight="700" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                  Fee Installments
                </Typography>
              </Box>

              <Box p={1}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <Button variant="contained" color="primary" size="small" onClick={handleMultiPay}
                    disabled={selectedPayments.length === 0} sx={{ borderRadius: 2, fontSize: '0.75rem', px: 3 }} >
                    Pay Selected ({selectedPayments.length})
                  </Button>
                </Stack>
                <TableContainer>
                  {structuredHeads.map((head) => {
                    let remainingDiscount = head.headId === "74be50bf-71ea-4ad1-9a12-64fd4ab96fb5"
                      ? parseFloat(totalDiscount || 0) : 0;
                    const processedInstallments = [...head.installments].map((inst) => ({
                      ...inst, originalAmount: parseFloat(inst.amount || 0),
                    }))
                      .reverse().map((inst) => {
                        let discountAmount = 0;
                        if (remainingDiscount > 0) {
                          if (remainingDiscount >= inst.originalAmount) {
                            discountAmount = inst.originalAmount;
                            remainingDiscount -= inst.originalAmount;
                          } else {
                            discountAmount = remainingDiscount;
                            remainingDiscount = 0;
                          }
                        }
                        return {
                          ...inst,
                          discountAmount,
                          finalAmount: Math.max(
                            0,
                            inst.originalAmount - discountAmount
                          ),
                        };
                      }).reverse();

                    return (
                      <Paper key={head.headId} variant="outlined" sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: '#fcfcfd',
                        mb: 2
                      }}
                      >
                        <Box sx={{
                          px: 1, py: 1, borderBottom: '1px solid #e2e8f0',
                          bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between'
                        }} >
                          <Typography variant="caption" fontWeight="bold" color="primary" > {head.name} </Typography>
                        </Box>

                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 800 }}> NAME </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 800 }} > TOTAL </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 800 }} > BALANCE </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 800 }} >ACTION </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 800 }} >SELECT </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {processedInstallments.map((inst, i) => {
                              const installmentAmount = parseFloat(inst.finalAmount || 0);
                              const { paid } = getInstallmentStatus(head.headId,
                                inst.index,
                                installmentAmount
                              );
                              const remaining = Math.max(0, installmentAmount - paid);
                              const isPaid = remaining <= 0;
                              let isLocked = false;
                              if (i > 0) {
                                const prevInst = processedInstallments[i - 1];
                                const prevAmount = parseFloat(prevInst.finalAmount || 0);
                                const prevStatus = getInstallmentStatus(
                                  head.headId,
                                  prevInst.index,
                                  prevAmount
                                );
                                if (prevStatus.remaining > 0) {
                                  isLocked = true;
                                }
                              }

                              return (
                                <TableRow
                                  key={i}
                                  hover
                                  sx={{
                                    '& td': {
                                      py: 0.5,
                                      borderBottom: '1px solid #f1f5f9'
                                    }
                                  }}
                                >
                                  {/* NAME */}
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600
                                      }}
                                    >
                                      {inst.name}
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: '0.65rem' }}
                                    >
                                      Due: {formatDate(inst.dueDate)}
                                    </Typography>
                                  </TableCell>

                                  {/* TOTAL */}
                                  <TableCell align="right">

                                    {inst.discountAmount > 0 ? (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            textDecoration: 'line-through',
                                            color: 'text.secondary',
                                            display: 'block'
                                          }}
                                        >
                                          ₹{inst.originalAmount}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 700,
                                            color: 'success.main'
                                          }}
                                        >
                                          ₹{installmentAmount}
                                        </Typography>

                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'error.main',
                                            fontWeight: 600
                                          }}
                                        >
                                          Discount ₹{inst.discountAmount}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        ₹{installmentAmount}
                                      </Typography>
                                    )}
                                  </TableCell>

                                  {/* BALANCE */}
                                  <TableCell align="right">
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        color: isPaid
                                          ? 'success.main'
                                          : 'error.main',
                                        bgcolor: isPaid
                                          ? '#f0fdf4'
                                          : '#fef2f2',
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      ₹{remaining}
                                    </Typography>
                                  </TableCell>

                                  {/* ACTION */}
                                  <TableCell align="right">
                                    <Button
                                      variant={isPaid ? "text" : "contained"}
                                      size="small"
                                      disabled={isPaid || isLocked}
                                      onClick={() =>
                                        openPaymentModal({
                                          headId: head.headId,
                                          installmentIndex: inst.index,
                                          remaining,
                                          amount: installmentAmount
                                        })
                                      }
                                      sx={{
                                        fontSize: '0.65rem',
                                        py: 0,
                                        textTransform: 'none',
                                        minWidth: isPaid
                                          ? 'unset'
                                          : '70px'
                                      }}
                                    >
                                      {isPaid
                                        ? 'Settled'
                                        : isLocked
                                          ? 'Locked'
                                          : 'Pay Now'}
                                    </Button>
                                  </TableCell>

                                  {/* CHECKBOX */}
                                  <TableCell align="center">
                                    {!isPaid && (
                                      <Checkbox
                                        size="small"
                                        checked={selectedPayments.some(
                                          p =>
                                            p.headId === head.headId &&
                                            p.installmentIndex === inst.index
                                        )}
                                        onChange={(e) => {

                                          const item = {
                                            headId: head.headId,
                                            installmentIndex: inst.index,
                                            remaining
                                          };

                                          if (e.target.checked) {
                                            setSelectedPayments(prev => [
                                              ...prev,
                                              item
                                            ]);
                                          } else {
                                            setSelectedPayments(prev =>
                                              prev.filter(
                                                p =>
                                                  !(
                                                    p.headId === head.headId &&
                                                    p.installmentIndex ===
                                                    inst.index
                                                  )
                                              )
                                            );
                                          }
                                        }}
                                        sx={{ p: 0.5 }}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Paper>
                    );
                  })}
                </TableContainer>
              </Box>
            </Card>
          </Grid>

        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 3, mt: 2 }}>
            <Box bgcolor="#1e293b" p={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" color="white" fontWeight="bold">
                Recent Transaction History
              </Typography>
              <Chip label={`${paymentHistory.length} Total Receipts`} size="small" sx={{ color: 'white', borderColor: 'white' }} variant="outlined" />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell>Sr No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Receipt No</TableCell>
                    <TableCell>Book Receipt</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Ledger</TableCell> {/* Added Ledger Header */}
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">No transactions found</TableCell> {/* Adjusted colSpan */}
                    </TableRow>
                  ) : (
                    paymentHistory
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((rec, idx) => (
                        <TableRow key={rec.receiptNo || idx}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{formatDate(rec.date)}</TableCell>
                          <TableCell align="right">₹{rec.paidAmount?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {rec.receiptNo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {rec.bookReceiptNo || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>{rec.payMode}</TableCell>
                          <TableCell>{rec.bankName || "-"}</TableCell>

                          {/* Display human readable Ledger Name */}
                          <TableCell>
                            {ledgers.find((l) => l.id === rec.ledger)?.ledgername || rec.ledger || "-"}
                          </TableCell>

                          <TableCell>{rec.note || "-"}</TableCell>

                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">

                              {/* Button 1: Quick Info Edit (Direct Update - No Approval) */}
                              {/* <Tooltip title="Quick Edit Fields">
                                <IconButton size="small" color="info" onClick={() => handleDirectFieldUpdate(rec)}>
                                  <Edit fontSize="inherit" />
                                </IconButton>
                              </Tooltip> */}

                              {/* Button 2: Amount Edit (Requires Approval) */}
                              <Tooltip title="Request Amount Change">
                                <IconButton size="small" color="warning" onClick={() => requestEdit(rec)}>
                                  <AssignmentTurnedIn fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                              {/* Button 3: Delete */}
                              <Tooltip title="Request Delete">
                                <IconButton size="small" color="error" onClick={() => requestDelete(rec)}>
                                  <Delete fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                            </Stack>
                          </TableCell>


                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Paper>

      <Dialog
        open={showPayModal}
        onClose={() => setShowPayModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: { borderRadius: 15, padding: '10px' }
        }}
      >
        <DialogTitle style={{ fontWeight: 'bold', color: '#b22222' }}>
          Collect Installment Payment
        </DialogTitle>

        <DialogContent dividers>
          <Grid className="row" spacing={3} sx={{ mt: 1 }}>
            {/* Amount Field */}
            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                size="small"
                label="Payment Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={paymentDetails.paidAmount}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, paidAmount: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            {/* Payment Mode */}
            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                select
                size="small"
                label="Payment Mode"
                fullWidth
                value={paymentDetails.payMode || 'Cash'}
                onChange={(e) =>
                  setPaymentDetails({
                    ...paymentDetails,
                    payMode: e.target.value
                  })
                }
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Online">Online / UPI</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
                <MenuItem value="Transfer">Bank Transfer</MenuItem>
              </TextField>
            </Grid>

            {/* BANK SELECTION */}
            {["Online", "Cheque", "Transfer"].includes(paymentDetails.payMode) && (
              <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
                <TextField
                  select
                  label="Select Bank"
                  fullWidth
                  size="small"
                  value={paymentDetails.bankId || ""}
                  onChange={(e) => {

                    const bank = banks.find(b => b.id === e.target.value);

                    setPaymentDetails({
                      ...paymentDetails,
                      bankId: bank?.id || "",
                      bankName: bank?.bankname || "",
                      bankBranch: bank?.branch || "",
                      bankCode: bank?.code || ""
                    });

                  }}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankname}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* TRANSACTION ID */}
            {paymentDetails.payMode === "Online" && (
              <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
                <TextField
                  label="Transaction ID"
                  fullWidth
                  size="small"
                  value={paymentDetails.transactionId || ""}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      transactionId: e.target.value
                    })
                  }
                />
              </Grid>
            )}

            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                select
                size="small"
                label="Ledger"
                fullWidth
                value={paymentDetails.ledger}
                onChange={(e) =>
                  setPaymentDetails({
                    ...paymentDetails,
                    ledger: e.target.value
                  })
                }
              >
                {ledgers.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.ledgername}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* CHEQUE NUMBER */}
            {paymentDetails.payMode === "Cheque" && (
              <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
                <TextField
                  label="Cheque No"
                  fullWidth
                  size="small"
                  value={paymentDetails.chequeNo || ""}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      chequeNo: e.target.value
                    })
                  }
                />
              </Grid>
            )}

            {/* Receipt No */}
            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                label="Receipt No"
                fullWidth size="small"
                value={paymentDetails.receiptNo}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, receiptNo: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                label="Book Receipt No"
                fullWidth
                size="small"
                value={paymentDetails.bookReceiptNo || ""}
                onChange={(e) =>
                  setPaymentDetails({
                    ...paymentDetails,
                    bookReceiptNo: e.target.value
                  })
                }
                placeholder="Enter Book Receipt No"
              />
            </Grid>

            {/* Payment Date */}
            <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
              <TextField
                label="Payment Date"
                type="date"
                fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={paymentDetails.date}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, date: e.target.value })}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <Autocomplete
                freeSolo
                options={feeHeadMaster.map(h => h.fee_heads)}
                value={paymentDetails.note}
                onInputChange={(event, newValue) => {
                  setPaymentDetails({
                    ...paymentDetails,
                    note: newValue
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fee Head / Remarks"
                    size="small"
                    placeholder="Select Fee Head or type remarks"
                    fullWidth
                  />
                )}
              />
            </Grid>
            
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField
                select
                size="small"
                label="Send WhatsApp?"
                fullWidth
                value={sendWhatsapp ? "yes" : "no"}
                onChange={(e) => setSendWhatsapp(e.target.value === "yes")}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowPayModal(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleFinalSubmitPayment}
            variant="contained"
            startIcon={
              saving
                ? <CircularProgress size={18} color="inherit" />
                : <Save />
            }
            disabled={saving}
            sx={{ bgcolor: '#b22222', '&:hover': { bgcolor: '#8b0000' } }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Feecollection;